import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Dual-handle price range slider. Uses two native range inputs stacked —
 * accessible by default, keyboard-friendly, no external dep.
 */
@Component({
  selector: 'vh-price-range',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="vh-price-range__track">
      <div
        class="vh-price-range__fill"
        [style.left.%]="fillLeft()"
        [style.right.%]="fillRight()"
      ></div>
      <input
        type="range"
        class="vh-price-range__input vh-price-range__input--min"
        [min]="min()"
        [max]="max()"
        [step]="step()"
        [ngModel]="low()"
        (ngModelChange)="onLow($event)"
        [attr.aria-label]="'Minimum ' + label()"
      />
      <input
        type="range"
        class="vh-price-range__input vh-price-range__input--max"
        [min]="min()"
        [max]="max()"
        [step]="step()"
        [ngModel]="high()"
        (ngModelChange)="onHigh($event)"
        [attr.aria-label]="'Maximum ' + label()"
      />
    </div>
    <div class="vh-price-range__values">
      <span class="vh-price-range__value">{{ format(low()) }}</span>
      <span class="vh-price-range__value">{{ format(high()) }}</span>
    </div>
  `,
  styleUrl: './price-range.component.scss',
})
export class VhPriceRangeComponent {
  readonly min = input<number>(0);
  readonly max = input<number>(1000);
  readonly step = input<number>(1);
  readonly value = input<readonly [number, number]>([0, 1000]);
  readonly label = input<string>('price');
  readonly currency = input<string>('INR');
  readonly locale = input<string>('en-IN');

  readonly valueChange = output<[number, number]>();

  protected readonly low = signal(0);
  protected readonly high = signal(1000);

  protected readonly fillLeft = computed(() => {
    const range = this.max() - this.min();
    if (range <= 0) return 0;
    return ((this.low() - this.min()) / range) * 100;
  });

  protected readonly fillRight = computed(() => {
    const range = this.max() - this.min();
    if (range <= 0) return 0;
    return ((this.max() - this.high()) / range) * 100;
  });

  constructor() {
    effect(() => {
      const [lo, hi] = this.value();
      this.low.set(lo);
      this.high.set(hi);
    });
  }

  onLow(next: number | string): void {
    const n = Number(next);
    const clamped = Math.min(n, this.high());
    this.low.set(clamped);
    this.valueChange.emit([clamped, this.high()]);
  }

  onHigh(next: number | string): void {
    const n = Number(next);
    const clamped = Math.max(n, this.low());
    this.high.set(clamped);
    this.valueChange.emit([this.low(), clamped]);
  }

  protected format(v: number): string {
    try {
      return new Intl.NumberFormat(this.locale(), {
        style: 'currency',
        currency: this.currency(),
        maximumFractionDigits: 0,
      }).format(v);
    } catch {
      return String(v);
    }
  }
}
